import { FlaskConical } from 'lucide-react'
import type { CreatedProduct } from '@/types/products'
import {
  formatLimsValue,
  isMissingPerKgOnly,
  limsDerivedFrom,
  limsRowsOf,
  limsSourceOf,
  LIMS_RESULT_GROUPS,
} from '@/lib/limsResults'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * A product's LIMS/LIS results — read only, always.
 *
 * These are calculated, not entered: the server derives every one of them from
 * what the lab measured on the source product, in the same transaction as the
 * split that produced the bag. There is deliberately no edit control anywhere
 * on this screen. A dose that a person can type over is not a dose that can be
 * traced back to a measurement.
 *
 * ClickUp z8nrz7dkvf §4.
 */
export function LimsDetailsDialog({
  product,
  onClose,
}: {
  product: CreatedProduct | null
  onClose: () => void
}) {
  const stored = product?.limsValues
  const source = limsSourceOf(stored)
  const derivedFrom = limsDerivedFrom(stored)
  const groups = LIMS_RESULT_GROUPS.map(group => ({
    ...group,
    rows: limsRowsOf(stored, group.keys),
  })).filter(group => group.rows.length > 0)

  return (
    <Dialog open={!!product} onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[90vh] min-w-0 overflow-y-auto sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <FlaskConical className='h-4 w-4 text-muted-foreground' />
            LIMS results
          </DialogTitle>
          <DialogDescription>
            {product ? `DIN ${product.din}` : ''}
            {product?.volumeMl != null ? ` · ${product.volumeMl} mL` : ''}
            {' — calculated from the lab’s measurements. Read only.'}
          </DialogDescription>
        </DialogHeader>

        {/*
          Until the LIMS endpoint exists the measurements are synthetic, and a
          reviewer has to be able to tell a made-up dose from a measured one —
          nothing else about the number does.
        */}
        {source === 'mock' && (
          <p className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
            These readings come from a stand-in source, not a real instrument. The LIMS
            integration is not connected yet.
          </p>
        )}

        {/*
          The one absence worth explaining. Every other missing result is
          simply left out, but a table with no dose column at all reads as the
          calculation having failed rather than as an input being absent.
        */}
        {isMissingPerKgOnly(stored) && (
          <p className='rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground'>
            Per-kg doses are not shown because no recipient weight is recorded on this
            procedure. Enter it on the patient chart — the field named{' '}
            <span className='font-mono'>Recipient Weight (kg)</span> — and split again to see
            them.
          </p>
        )}

        {groups.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            No lab results recorded for this product.
          </p>
        ) : (
          <div className='space-y-4'>
            {groups.map(group => (
              <div key={group.title}>
                <h3 className='mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  {group.title}
                </h3>
                <dl className='divide-y rounded-lg border'>
                  {group.rows.map(row => (
                    <div key={row.key} className='flex items-baseline justify-between px-3 py-2'>
                      <dt className='text-sm text-muted-foreground'>{row.label}</dt>
                      <dd className='font-mono text-sm tabular-nums'>
                        {formatLimsValue(row.value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        )}

        {/*
          Only shown for an aliquot. The results on a split bag are derived
          from the bag it came out of, and a reviewer chasing a figure needs to
          know which one that was.
        */}
        {derivedFrom && (
          <p className='text-xs text-muted-foreground'>
            Recalculated from the product this was split from.
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
